import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Upload, Download, Package, DollarSign, Warehouse, 
  FileSpreadsheet, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Eye, Trash2, Store, Search, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Vendor Selector Component
function VendorSelector({ selectedVendor, onSelect }) {
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axiosInstance.get('/api/admin/vendors');
      const vendorsList = response.data.vendors || response.data || [];
      setVendors(vendorsList.filter(v => v.status === 'approved') || []);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const filteredVendors = vendors.filter(v => 
    v.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    v.phone?.includes(search)
  );

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Store className="w-4 h-4" /> Select Vendor
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors..."
              className="pl-10"
            />
          </div>
          
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading vendors...</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {filteredVendors.map((vendor) => (
                <button
                  key={vendor.id}
                  onClick={() => onSelect(vendor)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedVendor?.id === vendor.id 
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-500' 
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{vendor.business_name}</p>
                      <p className="text-xs text-gray-500 truncate">{vendor.phone}</p>
                    </div>
                    {selectedVendor?.id === vendor.id && (
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {selectedVendor && (
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
              <Badge className="bg-green-600">Selected</Badge>
              <span className="font-medium">{selectedVendor.business_name}</span>
              <Button variant="ghost" size="sm" onClick={() => onSelect(null)} className="ml-auto">
                Clear
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Product Selector Component
function ProductSelector({ vendorId, selectedProducts, onSelect }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vendorId) {
      fetchProducts();
    } else {
      setProducts([]);
    }
  }, [vendorId]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/products?vendor_id=${vendorId}&limit=200`);
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleProduct = (product) => {
    const isSelected = selectedProducts.some(p => p._id === product._id);
    if (isSelected) {
      onSelect(selectedProducts.filter(p => p._id !== product._id));
    } else {
      onSelect([...selectedProducts, product]);
    }
  };

  const selectAll = () => onSelect(filteredProducts);
  const clearAll = () => onSelect([]);

  if (!vendorId) {
    return (
      <Card className="mb-6">
        <CardContent className="py-8 text-center text-gray-500">
          Select a vendor first to view their products
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Package className="w-4 h-4" /> Select Products
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
            <Button variant="outline" size="sm" onClick={clearAll}>Clear</Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or SKU..."
              className="pl-10"
            />
          </div>
          
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No products found for this vendor</div>
          ) : (
            <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
              {filteredProducts.map((product) => {
                const isSelected = selectedProducts.some(p => p._id === product._id);
                return (
                  <div
                    key={product._id}
                    onClick={() => toggleProduct(product)}
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                      isSelected ? 'bg-green-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <img 
                      src={product.images?.[0] || 'https://via.placeholder.com/40'} 
                      alt="" 
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">SKU: {product.sku || 'N/A'} | Stock: {product.inventory}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">₹{product.sale_price || product.price}</p>
                      {product.sale_price && (
                        <p className="text-xs text-gray-400 line-through">₹{product.price}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {selectedProducts.length > 0 && (
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
              <Badge className="bg-green-600">{selectedProducts.length} Selected</Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Bulk Update Form
function BulkUpdateForm({ selectedProducts, type, onComplete }) {
  const [updateValue, setUpdateValue] = useState('');
  const [updateType, setUpdateType] = useState('set'); // set, increase, decrease, percent
  const [processing, setProcessing] = useState(false);

  const handleBulkUpdate = async () => {
    if (!updateValue || selectedProducts.length === 0) {
      toast.error('Enter a value and select products');
      return;
    }

    setProcessing(true);
    try {
      const updates = selectedProducts.map(product => {
        let newValue;
        const currentValue = type === 'inventory' ? product.inventory : (product.sale_price || product.price);
        
        switch (updateType) {
          case 'increase':
            newValue = currentValue + parseFloat(updateValue);
            break;
          case 'decrease':
            newValue = Math.max(0, currentValue - parseFloat(updateValue));
            break;
          case 'percent':
            newValue = Math.round(currentValue * (1 + parseFloat(updateValue) / 100));
            break;
          default:
            newValue = parseFloat(updateValue);
        }

        if (type === 'inventory') {
          return { product_id: product._id, inventory: Math.max(0, Math.round(newValue)) };
        } else {
          return { 
            product_id: product._id, 
            price: updateType === 'set' ? parseFloat(updateValue) : product.price,
            sale_price: newValue
          };
        }
      });

      const endpoint = type === 'inventory' 
        ? '/api/admin/products/bulk-inventory'
        : '/api/admin/products/bulk-price';

      await axiosInstance.put(endpoint, { updates });
      toast.success(`Updated ${updates.length} products!`);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {type === 'inventory' ? 'Update Inventory' : 'Update Prices'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Update Type</Label>
            <select
              value={updateType}
              onChange={(e) => setUpdateType(e.target.value)}
              className="w-full mt-1 p-2 border rounded-lg"
            >
              <option value="set">Set to Value</option>
              <option value="increase">Increase by</option>
              <option value="decrease">Decrease by</option>
              <option value="percent">Change by %</option>
            </select>
          </div>
          <div>
            <Label>
              {updateType === 'percent' ? 'Percentage (%)' : type === 'inventory' ? 'Quantity' : 'Amount (₹)'}
            </Label>
            <Input
              type="number"
              value={updateValue}
              onChange={(e) => setUpdateValue(e.target.value)}
              placeholder={updateType === 'percent' ? 'e.g., 10 for +10%' : 'Enter value'}
              className="mt-1"
            />
          </div>
        </div>

        <Button
          onClick={handleBulkUpdate}
          disabled={processing || selectedProducts.length === 0 || !updateValue}
          className="w-full bg-green-600 hover:bg-green-700"
        >
          {processing ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
          ) : (
            <>Update {selectedProducts.length} Products</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// CSV Upload Component for Admin
function AdminCSVUpload({ type }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const generateTemplate = () => {
    if (type === 'products') {
      return `vendor_id,name,description,price,sale_price,stock,category,sku,weight,brand,fssai_number,ingredients,net_quantity,container_name,manufacturer,manufacturer_address,manufacturing_date,shelf_life,storage_instructions,country_of_origin,contact_details,image_url_1,image_url_2
"VENDOR_ID_HERE","Product Name","Description",999,799,100,"Category","SKU-001",500,"Brand","12345678901234","Ingredients","500g","Box","Manufacturer","Address","2025-01-01","6 months","Store cool","India","contact@example.com","https://image1.jpg",""\n`;
    }
    return '';
  };

  const downloadTemplate = () => {
    const blob = new Blob([generateTemplate()], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin_bulk_${type}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const values = [];
      let current = '';
      let inQuotes = false;
      for (let char of line) {
        if (char === '"') inQuotes = !inQuotes;
        else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
        else current += char;
      }
      values.push(current.trim());
      const obj = {};
      headers.forEach((header, i) => { obj[header] = values[i]?.replace(/^"|"$/g, '') || ''; });
      return obj;
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => setPreview(parseCSV(event.target.result));
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || preview.length === 0) return;
    setProcessing(true);
    let success = 0, failed = 0;
    const errors = [];

    for (const row of preview) {
      try {
        const images = [row.image_url_1, row.image_url_2].filter(Boolean);
        const productData = {
          vendor_id: row.vendor_id,
          name: row.name,
          description: row.description,
          price: parseFloat(row.price) || 0,
          sale_price: row.sale_price ? parseFloat(row.sale_price) : null,
          inventory: parseInt(row.stock) || 0,
          category_id: row.category,
          sku: row.sku,
          weight: parseFloat(row.weight) || 0,
          images,
          brand: row.brand || null,
          fssai_number: row.fssai_number || null,
          ingredients: row.ingredients || null,
          net_quantity: row.net_quantity || null,
          container_name: row.container_name || null,
          manufacturer: row.manufacturer || null,
          manufacturer_address: row.manufacturer_address || null,
          manufacturing_date: row.manufacturing_date || null,
          shelf_life: row.shelf_life || null,
          storage_instructions: row.storage_instructions || null,
          country_of_origin: row.country_of_origin || 'India',
          contact_details: row.contact_details || null
        };
        await axiosInstance.post('/api/admin/products', productData);
        success++;
      } catch (err) {
        failed++;
        errors.push(`${row.name}: ${err.response?.data?.detail || err.message}`);
      }
    }

    setResults({ success, failed, errors });
    if (success > 0) toast.success(`Created ${success} products!`);
    setProcessing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div>
          <h3 className="font-semibold">CSV Upload</h3>
          <p className="text-sm text-gray-600">Upload products for any vendor via CSV</p>
        </div>
        <Button variant="outline" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" /> Template
        </Button>
      </div>

      <Input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} />

      {preview.length > 0 && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {Object.keys(preview[0]).slice(0, 6).map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.slice(0, 3).map((row, i) => (
                <tr key={i} className="border-t">
                  {Object.values(row).slice(0, 6).map((v, j) => (
                    <td key={j} className="px-3 py-2 truncate max-w-[150px]">{v || '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 p-2">{preview.length} rows total</p>
        </div>
      )}

      {results && (
        <div className="p-3 bg-gray-50 rounded-lg flex gap-3">
          <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />{results.success}</Badge>
          {results.failed > 0 && <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />{results.failed}</Badge>}
        </div>
      )}

      <Button onClick={handleUpload} disabled={processing || preview.length === 0} className="w-full bg-green-600">
        {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {processing ? 'Processing...' : 'Upload Products'}
      </Button>
    </div>
  );
}

export default function AdminBulkOperations() {
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('inventory');

  const handleComplete = () => {
    setSelectedProducts([]);
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bulk Operations</h1>
        <p className="text-gray-600 mt-1">Manage vendor products in bulk - update inventory, prices, or create new products</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border p-1 rounded-xl shadow-sm">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <Warehouse className="w-4 h-4 mr-2" /> Inventory
          </TabsTrigger>
          <TabsTrigger value="price" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <DollarSign className="w-4 h-4 mr-2" /> Prices
          </TabsTrigger>
          <TabsTrigger value="products" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <Package className="w-4 h-4 mr-2" /> New Products
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="space-y-6">
          <VendorSelector selectedVendor={selectedVendor} onSelect={(v) => { setSelectedVendor(v); setSelectedProducts([]); }} />
          <ProductSelector vendorId={selectedVendor?.id} selectedProducts={selectedProducts} onSelect={setSelectedProducts} />
          <BulkUpdateForm selectedProducts={selectedProducts} type="inventory" onComplete={handleComplete} />
        </TabsContent>

        <TabsContent value="price" className="space-y-6">
          <VendorSelector selectedVendor={selectedVendor} onSelect={(v) => { setSelectedVendor(v); setSelectedProducts([]); }} />
          <ProductSelector vendorId={selectedVendor?.id} selectedProducts={selectedProducts} onSelect={setSelectedProducts} />
          <BulkUpdateForm selectedProducts={selectedProducts} type="price" onComplete={handleComplete} />
        </TabsContent>

        <TabsContent value="products" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <AdminCSVUpload type="products" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
